import React, { useState } from 'react';
import '../../styles/components/FormPage.css';
import { Input as FormInput } from '../FormField';
import { apiCreateVenueFull } from '../../services/VenueApiService';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors, useClearErrorsOnEdit } from './_shared';
import { PHONE_PLACEHOLDER } from '../../constants/placeholders';

interface VenueFormPageProps {
  onSave: (venueId: string) => void;
  onCancel: () => void;
}

export const VenueFormPage: React.FC<VenueFormPageProps> = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: '', address: '', capacity: '', phone: '', email: '', website: '', amenities: '',
    capacitySeatedBanquet: '', capacitySeatedConference: '', capacityStanding: '',
  });
  const { showError } = useNotifications();
  const fieldErrors = useFieldErrors();
  const [saving, setSaving] = useState(false);
  // The form's own "name is required" check; a server message for the same
  // field comes from fieldErrors.
  const [nameMissing, setNameMissing] = useState<string | null>(null);

  const set = (field: keyof typeof form, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'name') setNameMissing(null);
  };
  // API field names differ from the form's for the capacity fields.
  useClearErrorsOnEdit(form, fieldErrors, {
    capacitySeatedBanquet: 'capacity_seated_banquet',
    capacitySeatedConference: 'capacity_seated_conference',
    capacityStanding: 'capacity_standing',
  });

  const handleSave = async () => {
    if (!form.name.trim()) { setNameMissing('Venue name is required'); return; }
    setSaving(true);
    setNameMissing(null);
    fieldErrors.clear();
    try {
      const { id } = await apiCreateVenueFull({
        name: form.name.trim(),
        address: form.address || undefined,
        capacity: form.capacity ? parseInt(form.capacity) : undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        website: form.website || undefined,
        amenities: form.amenities.split(',').map(s => s.trim()).filter(Boolean),
        capacitySeatedBanquet: form.capacitySeatedBanquet ? parseInt(form.capacitySeatedBanquet) : undefined,
        capacitySeatedConference: form.capacitySeatedConference ? parseInt(form.capacitySeatedConference) : undefined,
        capacityStanding: form.capacityStanding ? parseInt(form.capacityStanding) : undefined,
      });
      onSave(id);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to create venue. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="inline-form-page">
      <FormInput
        label="Venue Name" type="text" value={form.name}
        onChange={e => set('name', e.target.value)}
        placeholder="e.g. NACCAS Asokoro"
        error={nameMissing ?? fieldErrors.errorFor('name')}
        required
      />
      <FormInput
        label="Address" type="text" value={form.address}
        onChange={e => set('address', e.target.value)}
        error={fieldErrors.errorFor('address')}
        placeholder="e.g. Asokoro, Abuja"
      />

      <p className="form-section-title">Capacity</p>
      <div className="form-row">
        <FormInput label="General Capacity" type="number" value={form.capacity}
          error={fieldErrors.errorFor('capacity')}
          onChange={e => set('capacity', e.target.value)} placeholder="e.g. 800" />
        <FormInput label="Seated (Banquet)" type="number" value={form.capacitySeatedBanquet}
          error={fieldErrors.errorFor('capacity_seated_banquet')}
          onChange={e => set('capacitySeatedBanquet', e.target.value)} placeholder="e.g. 600" />
      </div>
      <div className="form-row">
        <FormInput label="Seated (Conference)" type="number" value={form.capacitySeatedConference}
          error={fieldErrors.errorFor('capacity_seated_conference')}
          onChange={e => set('capacitySeatedConference', e.target.value)} placeholder="e.g. 400" />
        <FormInput label="Standing" type="number" value={form.capacityStanding}
          error={fieldErrors.errorFor('capacity_standing')}
          onChange={e => set('capacityStanding', e.target.value)} placeholder="e.g. 1000" />
      </div>

      <p className="form-section-title">Contact</p>
      <div className="form-row">
        <FormInput label="Phone" type="text" value={form.phone}
          error={fieldErrors.errorFor('phone')}
          onChange={e => set('phone', e.target.value)} placeholder={PHONE_PLACEHOLDER} />
        <FormInput label="Email" type="email" value={form.email}
          error={fieldErrors.errorFor('email')}
          onChange={e => set('email', e.target.value)} placeholder="venue@example.com" />
      </div>
      <FormInput label="Website" type="text" value={form.website}
        error={fieldErrors.errorFor('website')}
          onChange={e => set('website', e.target.value)} placeholder="https://…" />
      <FormInput label="Amenities" type="text" value={form.amenities}
        error={fieldErrors.errorFor('amenities')}
          onChange={e => set('amenities', e.target.value)} placeholder="Parking, Catering Kitchen…" />

      <div className="form-actions">
        <button type="button" className="header-action-btn primary" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Creating…' : 'Create Venue'}
        </button>
        <button type="button" className="header-action-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default VenueFormPage;
